import React, { useState, FC } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MobileMenu from './MobileMenu';
import type { NavigationView, NavigationItem, NavigationProps } from '../../types/navigation';
import { useRole } from '../../hooks/useRole';
import { NotificationBell } from '../Notifications/NotificationBell';
import { 
  ClockIcon, 
  ListBulletsIcon, 
  UserIcon, 
  DocumentTextIcon,
  ReceiptIcon,
  TrophyIcon,
  CalendarIcon,
  BuildingIcon,
  UsersIcon
} from '../icons';
import { Link } from 'react-router-dom';
import thirstyLogo from '../../assets/thirsty-logo.png';

const Navigation: FC<NavigationProps> = ({ currentView, onViewChange }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAuth();
  const { isAdmin } = useRole();

  const mainNavigation: NavigationItem[] = [
    {
      name: 'DTR',
      href: '/',
      view: 'dtr',
      icon: ClockIcon
    },
    {
      name: 'List',
      href: '/list',
      view: 'list',
      icon: ListBulletsIcon
    },
    {
      name: 'Profile',
      href: '/profile',
      view: 'profile',
      icon: UserIcon
    },
    {
      name: 'Thirsty Records',
      href: '/thirsty',
      view: 'thirsty',
      icon: DocumentTextIcon
    },
    {
      name: 'Payslips',
      href: '/payslips',
      view: 'payslips',
      icon: ReceiptIcon
    },
    {
      name: 'Leaderboard',
      href: '/leaderboard',
      view: 'leaderboard',
      icon: TrophyIcon
    }
  ];

  const staffAdminNavigation: NavigationItem[] = [
    {
      name: 'Staff Scheduler',
      href: '/admin/scheduler',
      view: 'scheduler',
      icon: CalendarIcon
    },
    {
      name: 'Leave Management',
      href: '/admin/leaves',
      view: 'leaves',
      icon: CalendarIcon
    }
  ];

  const adminOnlyNavigation: NavigationItem[] = [
    {
      name: 'Payroll Management',
      href: '/admin/payroll',
      view: 'admin',
      icon: DocumentTextIcon
    },
    {
      name: 'Branch Management',
      href: '/admin/branches',
      view: 'branches',
      icon: BuildingIcon
    },
    {
      name: 'Staff Profiles',
      href: '/admin/staff-profiles',
      view: 'staff-profiles',
      icon: UsersIcon
    },
    {
      name: 'Spin the Wheel',
      href: '/admin/spin-wheel',
      view: 'spin-wheel',
      icon: TrophyIcon
    }
  ];

  const navigation = [
    ...mainNavigation,
    ...staffAdminNavigation,
    ...(isAdmin ? adminOnlyNavigation : [])
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center">
          <img 
            src={thirstyLogo} 
            alt="Thirsty Logo" 
            className="h-12 w-auto"
          />
        </div>

        <div className="flex items-center gap-4">
          <NotificationBell />
          <button
            onClick={() => setIsMenuOpen(true)}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            aria-expanded="false"
          >
            <span className="sr-only">Open menu</span>
            <svg 
              className="h-6 w-6" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 6h16M4 12h16M4 18h16" 
              />
            </svg>
          </button>
        </div>

        <MobileMenu
          isOpen={isMenuOpen}
          navigation={navigation}
          currentView={currentView}
          onViewChange={onViewChange}
          onClose={() => setIsMenuOpen(false)}
          userEmail={user?.email}
        />
      </div>
    </nav>
  );
};

export default Navigation;